/**
 * Usage Analytics page component.
 * Displays usage tracking data with charts, metrics, and filters.
 */

import { useState } from 'react';
import { Button, Chip, CoordioDropdown, DatePicker, ShellHeader } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { useQuery } from '@tanstack/react-query';
import { usageService } from '../services/usageService';
import {
  AggregationPeriod,
  UsageType,
  type UsageAggregationDto,
  type UsageSummaryDto,
  type PagedUsageRecords,
} from '../types/usage';

// Charts
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';

/**
 * Usage Analytics page component.
 */
export const UsageAnalyticsPage = () => {
  const [usageType, setUsageType] = useState<string>('');
  const [startDate, setStartDate] = useState<Date>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30 days ago
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 50;

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Fetch usage summary
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery<UsageSummaryDto>({
    queryKey: ['usageSummary', usageType, startDate, endDate],
    queryFn: async () => {
      return usageService.getUsageSummary({
        usageType: usageType ? Number(usageType) as UsageType : undefined,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      });
    },
    enabled: !!startDate && !!endDate,
  });

  // Fetch usage records
  const {
    data: records,
    isLoading: recordsLoading,
    error: recordsError,
  } = useQuery<PagedUsageRecords>({
    queryKey: ['usageRecords', usageType, startDate, endDate, pageNumber],
    queryFn: async () => {
      return usageService.getUsageRecords({
        usageType: usageType ? Number(usageType) as UsageType : undefined,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        pageNumber,
        pageSize,
      });
    },
    enabled: !!startDate && !!endDate,
  });

  // Fetch aggregations (for future use - can be used for trend charts)
  const aggregationUsageType: UsageType = usageType ? (Number(usageType) as UsageType) : UsageType.LLMTokens;
  const {
    data: aggregations,
    isLoading: aggregationsLoading,
    error: aggregationsError,
  } = useQuery<UsageAggregationDto[]>({
    queryKey: ['usageAggregations', aggregationUsageType, startDate, endDate],
    queryFn: async () => {
      return usageService.getUsageAggregations({
        usageType: aggregationUsageType,
        period: AggregationPeriod.Daily,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      });
    },
    enabled: !!startDate && !!endDate,
  });

  const trendChartData = (aggregations ?? []).map((a) => ({
    date: new Date(a.periodStart).toLocaleDateString(),
    quantity: a.totalQuantity,
    cost: a.totalCost,
  }));

  const handleApplyFilters = () => {
    refetchSummary();
    setPageNumber(1);
  };

  const getUsageTypeLabel = (type: UsageType | number) => {
    const typeNum = typeof type === 'number' ? type : Number(type);
    switch (typeNum) {
      case UsageType.LLMTokens:
        return 'LLM Tokens';
      case UsageType.Storage:
        return 'Storage';
      case UsageType.ApiCalls:
        return 'API Calls';
      case UsageType.ToolExecutions:
        return 'Tool Executions';
      case UsageType.ChatCompaction:
        return 'Chat Compaction';
      case UsageType.ImageCompaction:
        return 'Image Compaction';
      case UsageType.ImageDescription:
        return 'Image Description';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="usage-analytics" data-testid="usage-analytics-detail-page">
      <ShellHeader
        title="Usage Analytics"
        description="Track and analyze usage of tokens, storage, API calls, and tool executions."
      />

      <PageContainer>
      {/* Filters */}
      <div className="mb-4 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Usage Type</label>
            <CoordioDropdown
              options={[
                { value: String(UsageType.LLMTokens), label: 'LLM Tokens' },
                { value: String(UsageType.Storage), label: 'Storage' },
                { value: String(UsageType.ApiCalls), label: 'API Calls' },
                { value: String(UsageType.ToolExecutions), label: 'Tool Executions' },
                { value: String(UsageType.ChatCompaction), label: 'Chat Compaction' },
                { value: String(UsageType.ImageCompaction), label: 'Image Compaction' },
                { value: String(UsageType.ImageDescription), label: 'Image Description' },
              ]}
              value={usageType || null}
              onChange={(val) => setUsageType(val ?? '')}
              placeholder="All Types"
              clearable
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Start Date</label>
            <DatePicker
              value={formatDate(startDate)}
              onValueChange={(val) => { if (val) setStartDate(new Date(val)); }}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">End Date</label>
            <DatePicker
              value={formatDate(endDate)}
              onValueChange={(val) => { if (val) setEndDate(new Date(val)); }}
            />
          </div>
          <div>
            <Button
              type="button"
              variant="default"
              className="w-full"
              onClick={handleApplyFilters}
              disabled={summaryLoading || recordsLoading}
              data-testid="usage-analytics-apply-filters-btn"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2">
            <p className="text-xs font-medium text-[var(--color-text-secondary)]">Total Quantity</p>
            <h4 className="text-base font-semibold">{formatNumber(summary.totalQuantity)}</h4>
          </div>
          <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2">
            <p className="text-xs font-medium text-[var(--color-text-secondary)]">Total Cost</p>
            <h4 className="text-base font-semibold text-[var(--color-accent)]">{formatCurrency(summary.totalCost)}</h4>
          </div>
          <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2">
            <p className="text-xs font-medium text-[var(--color-text-secondary)]">Record Count</p>
            <h4 className="text-base font-semibold">{formatNumber(summary.recordCount)}</h4>
          </div>
          <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2">
            <p className="text-xs font-medium text-[var(--color-text-secondary)]">Usage Type</p>
            <Chip label={getUsageTypeLabel(summary.usageType)} color="blue" className="mt-0.5" />
          </div>
        </div>
      )}

      {/* Loading State */}
      {(summaryLoading || recordsLoading || aggregationsLoading) && (
        <LoadingSkeleton rows={4} data-testid="usage-analytics-detail-loading" />
      )}

      {/* Error State */}
      {(summaryError || recordsError || aggregationsError) && (
        <InlineErrorPanel
          message={summaryError?.message || recordsError?.message || (aggregationsError as any)?.message || 'Failed to load usage data'}
          data-testid="usage-analytics-detail-error"
        />
      )}

      {/* Trend Charts */}
      <div className="mb-4 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
        <h6 className="mb-1 text-base font-semibold">Trends (Daily)</h6>
        <p className="mb-2 text-sm text-[var(--color-text-secondary)]">
          Showing quantity (left axis) and cost (right axis) for{' '}
          {aggregationUsageType === UsageType.LLMTokens
            ? 'LLM Tokens'
            : aggregationUsageType === UsageType.ApiCalls
              ? 'API Calls'
              : aggregationUsageType === UsageType.Storage
                ? 'Storage'
                : 'Tool Executions'}
          .
        </p>

        <div style={{ height: 320 }}>
          {trendChartData.length === 0 ? (
            <p className="text-sm text-[var(--color-text-secondary)]">
              No aggregation data available for the selected range.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="quantity" stroke="#7367F0" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="cost" stroke="#28C76F" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Breakdown by Agent */}
      {summary && summary.byAgent.length > 0 && (
        <div className="mb-4 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
          <h6 className="mb-2 text-base font-semibold">Usage by Agent</h6>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border-default)]">
                  <th className="px-3 py-2 text-left font-medium">Agent</th>
                  <th className="px-3 py-2 text-right font-medium">Quantity</th>
                  <th className="px-3 py-2 text-right font-medium">Cost</th>
                  <th className="px-3 py-2 text-right font-medium">Records</th>
                </tr>
              </thead>
              <tbody>
                {summary.byAgent.map((item) => (
                  <tr key={item.agentDefinitionId} className="border-b border-[var(--color-border-default)] last:border-b-0">
                    <td className="px-3 py-2">{item.agentDefinitionName}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(item.totalQuantity)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(item.totalCost)}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(item.recordCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Breakdown by Model */}
      {summary && summary.byModel.length > 0 && (
        <div className="mb-4 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
          <h6 className="mb-2 text-base font-semibold">Usage by Model</h6>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border-default)]">
                  <th className="px-3 py-2 text-left font-medium">Model</th>
                  <th className="px-3 py-2 text-right font-medium">Quantity</th>
                  <th className="px-3 py-2 text-right font-medium">Cost</th>
                  <th className="px-3 py-2 text-right font-medium">Records</th>
                </tr>
              </thead>
              <tbody>
                {summary.byModel.map((item) => (
                  <tr key={item.modelId} className="border-b border-[var(--color-border-default)] last:border-b-0">
                    <td className="px-3 py-2">{item.modelName}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(item.totalQuantity)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(item.totalCost)}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(item.recordCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Usage Records Table */}
      {records && (
        <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
          <h6 className="mb-2 text-base font-semibold">Usage Records</h6>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border-default)]">
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Agent</th>
                  <th className="px-3 py-2 text-left font-medium">Model</th>
                  <th className="px-3 py-2 text-right font-medium">Quantity</th>
                  <th className="px-3 py-2 text-right font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {records.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-sm text-[var(--color-text-secondary)]">
                      No usage records found
                    </td>
                  </tr>
                ) : (
                  records.items.map((record) => (
                    <tr key={record.id} className="border-b border-[var(--color-border-default)] last:border-b-0">
                      <td className="px-3 py-2">{new Date(record.recordDate).toLocaleDateString()}</td>
                      <td className="px-3 py-2">
                        <Chip label={getUsageTypeLabel(record.usageType)} color="blue" variant="outlined" />
                      </td>
                      <td className="px-3 py-2">{record.agentDefinitionName || '-'}</td>
                      <td className="px-3 py-2">{record.modelName || '-'}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(record.quantity)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(record.cost)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {records.totalCount > pageSize && (
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-[var(--color-text-secondary)]">
                Showing {((pageNumber - 1) * pageSize) + 1} to {Math.min(pageNumber * pageSize, records.totalCount)} of {formatNumber(records.totalCount)} records
              </p>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageNumber === 1}
                  onClick={() => setPageNumber(pageNumber - 1)}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageNumber * pageSize >= records.totalCount}
                  onClick={() => setPageNumber(pageNumber + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      </PageContainer>
    </div>
  );
};
